/**
 * PipelineTab — the Monthly Pipeline page: month header → GAM report card
 * (stage 0, with the page-level drop zone) → vertical stage timeline →
 * previous-months history. State and polling live in usePipelineController
 * (owned by FinancialsDashboard so the tab-label dot stays live everywhere).
 */

import React, { useEffect, useState } from 'react';
import { Button, Spinner } from '../../ui';
import { listEarningsCorrections, CorrectionListItem } from '../../../services/earningsCorrectionsApi';
import { GamUploadRequiresForceError } from '../../../services/adminApi';
import type { GamReportSourceMeta, PayoutTarget } from '../../../services/adminApi';
import GamReportUpload from '../GamReportUpload';
import { PipelineController } from './usePipelineController';
import PipelineMonthHeader from './PipelineMonthHeader';
import GamReportCard from './GamReportCard';
import PipelineStageList, { PipelineTabKey } from './PipelineStageList';
import PipelineHistoryStrip from './PipelineHistoryStrip';
import ConfirmStageModal, { ConfirmStep } from './ConfirmStageModal';
import { allStagesComplete, formatMonthDisplay, reportSetStatus } from './pipelineUtils';

interface PipelineTabProps {
  controller: PipelineController;
  onNavigateTab: (tab: PipelineTabKey) => void;
  /** Open Financials → Corrections for a month. */
  onViewCorrections?: (month: string) => void;
}

const fmtSignedUsd = (cents: number): string => {
  const abs = (Math.abs(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return cents > 0 ? `+$${abs}` : cents < 0 ? `-$${abs}` : '$0.00';
};

/** This month's share of each applied correction's net change, in cents. */
const monthNetCents = (corrections: readonly CorrectionListItem[], month: string): number =>
  corrections.reduce((sum, c) => {
    const m = c.summary.byMonth.find((b) => b.month === month);
    return sum + (m ? m.directSoldDeltaCents + m.adSenseDeltaCents : 0);
  }, 0);

const PipelineTab: React.FC<PipelineTabProps> = ({ controller, onNavigateTab, onViewCorrections }) => {
  const {
    selectedMonth,
    selectMonth,
    pipeline,
    uploadMeta,
    history,
    loading,
    error,
    actionBusy,
    phase,
  } = controller;

  // Pre-start drafts (used by Start; a live pipeline's mode comes from the server)
  const [draftDryRun, setDraftDryRun] = useState(false);
  const [draftAuto, setDraftAuto] = useState(true); // new runs default to Automatic
  const [uploadModal, setUploadModal] = useState<{
    open: boolean;
    files: File[] | null;
    intent?: 'add' | 'replace-all';
  }>({ open: false, files: null });
  const [confirmStep, setConfirmStep] = useState<ConfirmStep | null>(null);
  // Applied Earnings Corrections for the month: the payout sheets never see them.
  const [monthCorrections, setMonthCorrections] = useState<CorrectionListItem[]>([]);
  // Until the list loads, writing payout sheets asks first rather than assuming "none".
  const [correctionsLoad, setCorrectionsLoad] = useState<'loading' | 'loaded' | 'failed'>('loading');

  useEffect(() => {
    setMonthCorrections([]);
    setCorrectionsLoad('loading');
    if (!selectedMonth) return undefined;
    let cancelled = false;
    listEarningsCorrections({ status: 'applied', month: selectedMonth })
      .then(({ corrections }) => {
        if (cancelled) return;
        setMonthCorrections(corrections);
        setCorrectionsLoad('loaded');
      })
      .catch(() => {
        if (!cancelled) setCorrectionsLoad('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [selectedMonth]);

  // A restart should prefill the mode the month was running with
  useEffect(() => {
    if (pipeline) setDraftAuto(pipeline.autoAdvance === true);
  }, [pipeline]);

  const monthDisplay = formatMonthDisplay(selectedMonth || '');

  // Processing with a report missing leaves its delivery out of the month: ask first.
  const reportSet = reportSetStatus(uploadMeta, controller.expectedReports);
  const missingReportsNote =
    uploadMeta && reportSet.missing > 0
      ? `Only ${reportSet.count} of ${reportSet.expected} GAM reports are uploaded for ${monthDisplay} — ` +
        'delivery in the missing report won’t be processed or paid out. '
      : '';

  const handleStart = async () => {
    if (missingReportsNote && !window.confirm(`${missingReportsNote}Start processing anyway?`)) return;
    try {
      await controller.start(draftDryRun, draftAuto);
    } catch {
      // surfaced via controller.error
    }
  };

  const handleRestart = async () => {
    if (!pipeline) return;
    const prompt = allStagesComplete(pipeline)
      ? `${pipeline.monthDisplay} is already complete. Re-running re-processes the stored GAM upload and resets validation; ` +
        'the ChannelMeter confirmations and the generated payout sheets are kept — re-run only the sheets that depend on the new data. Continue?'
      : `Re-run DS processing for ${pipeline.monthDisplay}? Outputs are rewritten in place — no duplicates are created.`;
    if (!window.confirm(missingReportsNote + prompt)) return;
    try {
      // Restart preserves the month's dry-run/mode flags
      await controller.start(pipeline.dryRun === true, pipeline.autoAdvance === true, pipeline.month);
    } catch {
      // surfaced via controller.error
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel the current DS processing run?')) return;
    try {
      await controller.cancelProcessing();
    } catch {
      // surfaced via controller.error
    }
  };

  const handleDiscard = async () => {
    if (!pipeline?.dryRun) return;
    if (!window.confirm(`Discard the ${pipeline.monthDisplay} dry run? This deletes the pipeline so you can start over.`)) {
      return;
    }
    try {
      await controller.discardDryRun();
    } catch {
      // surfaced via controller.error
    }
  };

  const handleRemoveSource = async (source: GamReportSourceMeta) => {
    const others = (uploadMeta?.sources ?? []).filter((s) => s.sourceId !== source.sourceId);
    const ran = Boolean(pipeline && pipeline.stages.dsProcessing.status !== 'pending');
    if (
      !window.confirm(
        `Remove ${source.fileName} from ${monthDisplay}’s GAM reports? ` +
          `DS processing will read only ${others.map((s) => s.fileName).join(' + ') || 'the remaining reports'}.` +
          (ran ? ' Re-run processing afterwards to pick up the change.' : '')
      )
    ) {
      return;
    }
    try {
      await controller.removeSource(source.sourceId);
    } catch (err) {
      if (!(err instanceof GamUploadRequiresForceError)) return; // banner shows it
      if (!window.confirm(`${err.message}\n\nRemove ${source.fileName} anyway?`)) return;
      await controller.removeSource(source.sourceId, true).catch(() => {});
    }
  };

  const correctionsNet = monthNetCents(monthCorrections, selectedMonth || '');

  /**
   * Payout sheets always use ChannelMeter's paid amounts — say so before writing them for a
   * corrected month (or one whose corrections couldn't be checked). `lead` names the trigger
   * when the write is a side effect (Automatic mode generates right after the lock).
   */
  const okToWritePayouts = (lead = ''): boolean => {
    if (correctionsLoad === 'loaded' && monthCorrections.length === 0) return true;
    const n = monthCorrections.length;
    const about =
      n > 0
        ? `${monthDisplay} has ${n} applied Earnings Correction${n === 1 ? '' : 's'} (${fmtSignedUsd(correctionsNet)} vs ChannelMeter). `
        : `Couldn’t check whether ${monthDisplay} has applied Earnings Corrections. `;
    return window.confirm(
      lead +
        about +
        'The payout sheets are written from ChannelMeter’s paid amounts, not the corrected figures, and regenerating ' +
        'overwrites any hand edits in the existing tabs. Generate anyway?'
    );
  };

  const handleGeneratePayouts = async (targets?: PayoutTarget[]) => {
    if (!okToWritePayouts()) return;
    await controller.generatePayouts(targets);
  };

  const AUTO_LEAD =
    'In Automatic mode this generates the payout sheets right away (cancel and switch the month to Manual to hold them). ';

  const handleSetMode = (auto: boolean) => {
    const lockedAwaitingSheets =
      pipeline?.stages.cmLock.status === 'confirmed' && pipeline.stages.payoutSheets.status === 'pending';
    if (auto && lockedAwaitingSheets && !okToWritePayouts(AUTO_LEAD)) return;
    controller.setMode(auto).catch(() => {});
  };

  const handleConfirm = async (notes?: string) => {
    try {
      if (confirmStep === 'cmUpload') await controller.confirmUpload(notes);
      if (confirmStep === 'cmLock') {
        if (pipeline?.autoAdvance === true && !okToWritePayouts(AUTO_LEAD)) {
          setConfirmStep(null);
          return;
        }
        await controller.confirmLock();
      }
      setConfirmStep(null);
    } catch {
      setConfirmStep(null); // error banner shows the failure
    }
  };

  if (!selectedMonth || (loading && !pipeline && !uploadMeta)) {
    return (
      <div style={styles.loading}>
        <Spinner size={28} label="Loading pipeline…" />
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <PipelineMonthHeader
        month={selectedMonth}
        pipeline={pipeline}
        phase={phase}
        actionBusy={actionBusy}
        draftAuto={draftAuto}
        onDraftAutoChange={setDraftAuto}
        onSelectMonth={selectMonth}
        onSetMode={handleSetMode}
        onDiscardDryRun={handleDiscard}
      />

      {error && <div style={styles.errorBanner} role="alert">{error}</div>}

      {monthCorrections.length > 0 && (
        <div style={styles.correctionsNotice} role="note">
          <div>
            <strong>
              {monthDisplay} has {monthCorrections.length} applied Earnings Correction{monthCorrections.length === 1 ? '' : 's'}
            </strong>{' '}
            ({fmtSignedUsd(correctionsNet)} vs ChannelMeter). The Earnings tab, chat and client pages show the corrected
            figures. The payout sheets are always written from ChannelMeter’s paid amounts, and regenerating a sheet overwrites
            any hand edits in it.
          </div>
          {onViewCorrections && selectedMonth && (
            <Button variant="ghost" size="sm" onClick={() => onViewCorrections(selectedMonth)}>
              View corrections
            </Button>
          )}
        </div>
      )}

      <GamReportCard
        month={selectedMonth}
        uploadMeta={uploadMeta}
        expectedReports={controller.expectedReports}
        pipeline={pipeline}
        dsRunStats={controller.dsRunStats}
        onUpload={(files, intent) => setUploadModal({ open: true, files: files ?? null, intent })}
        onRemoveSource={handleRemoveSource}
        removingSourceId={actionBusy?.startsWith('removeSource:') ? actionBusy.slice('removeSource:'.length) : null}
        onRerunProcessing={handleRestart}
        rerunBusy={actionBusy === 'start'}
      />

      <PipelineStageList
        pipeline={pipeline}
        uploadMeta={uploadMeta}
        monthDisplay={monthDisplay}
        actionBusy={actionBusy}
        draftDryRun={draftDryRun}
        onDraftDryRunChange={setDraftDryRun}
        onStart={handleStart}
        onRestart={handleRestart}
        onCancelProcessing={handleCancel}
        onRunValidation={() => controller.runValidation().catch(() => {})}
        onConfirmStep={setConfirmStep}
        onGeneratePayouts={handleGeneratePayouts}
        onRerunStage1={controller.rerunStage1}
        onNavigateTab={onNavigateTab}
      />

      <PipelineHistoryStrip
        history={history}
        selectedMonth={selectedMonth}
        onSelect={selectMonth}
        onViewProcessingHistory={() => onNavigateTab('processing')}
      />

      <GamReportUpload
        open={uploadModal.open}
        onClose={() => setUploadModal({ open: false, files: null })}
        defaultMonth={selectedMonth}
        pipelineInProgress={Boolean(pipeline && pipeline.stages.dsProcessing.status !== 'pending')}
        initialFiles={uploadModal.files}
        intent={uploadModal.intent}
        onUploaded={controller.onUploaded}
      />

      <ConfirmStageModal
        step={confirmStep}
        monthDisplay={monthDisplay}
        busy={actionBusy === 'cmUpload' || actionBusy === 'cmLock'}
        onConfirm={handleConfirm}
        onClose={() => setConfirmStep(null)}
      />
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    maxWidth: 980,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: '64px 0',
  },
  correctionsNotice: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
    padding: '10px 14px',
    fontSize: 13,
    lineHeight: 1.45,
    color: 'var(--fg-1)',
    background: 'var(--warning-soft)',
    border: '1px solid var(--cg-saffron-200)',
    borderRadius: 'var(--radius-md)',
  },
  errorBanner: {
    padding: '10px 14px',
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--danger)',
    background: 'var(--danger-soft)',
    border: '1px solid var(--cg-terra-200)',
    borderRadius: 'var(--radius-md)',
    lineHeight: 1.45,
  },
};

export default PipelineTab;
